import { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';

// Types
import { AppDispatch } from 'types/RootState.types';
import { SessionManagerProps } from './SessionManager.types';

// Config
import sessionManagersState from './SessionManager.state';
import {
  APPS_AUTHENTICATED_HANDOVER,
  APPS_GUEST_HANDOVER,
  ANALYTICS_SAVE_APP_QUERY_PARAMS,
} from '@constants/actionConstants';
import { triggerAnalyticsEvent } from '@redux/actions/analytics/analyticsAction';
import { setAppGlobalTheme, showDisableSiteSpinner } from '@redux/actions/app/appActions';
import { handleAppsHandover, handleWebHandover } from '@redux/actions/bff/bffActions';
import { getAnalyticsQueryParams } from '@utils/app/analyticsQueryParams';
import { getHandoverParams } from '@utils/app/getHandoverParams';

// Components
import LoadingScreen from '@components/loading-screen';

const SessionManager = ({ children, location }: SessionManagerProps) => {
  const dispatch: AppDispatch = useDispatch();

  const {
    basketId,
    biom = '',
    email,
    envId,
    express,
    jsessionId,
    monetateId,
    theme,
    vid = '',
  } = getHandoverParams(location?.search);

  const {
    sessionHandoverComplete,
    sessionHandoverFailed,
    getLoginPageComplete,
    isAppsGuestHandover,
    isAppsAuthenticatedHandover,
    auth0AccessToken,
    requestAccessToken,
  } = useSelector(sessionManagersState);

  useEffect(() => {
    if (isAppsAuthenticatedHandover || isAppsGuestHandover) {
      const appQueryParamsToSave = getAnalyticsQueryParams(location?.search);
      if (appQueryParamsToSave) {
        dispatch(
          triggerAnalyticsEvent(ANALYTICS_SAVE_APP_QUERY_PARAMS, {
            queryParameters: appQueryParamsToSave,
          }),
        );
      }
    }

    if (isAppsAuthenticatedHandover) {
      dispatch(showDisableSiteSpinner());
      dispatch(setAppGlobalTheme({ theme }));
      return;
    }

    if (isAppsGuestHandover) {
      dispatch(showDisableSiteSpinner());
      dispatch(setAppGlobalTheme({ theme }));
      dispatch(
        handleAppsHandover({
          actionType: APPS_GUEST_HANDOVER,
          basketId,
          biom,
          email,
          environmentId: envId,
          express,
          jsessionId,
          monetateId,
          visitorId: vid,
          theme,
        }),
      );
      return;
    }

    if (!sessionHandoverComplete && !sessionHandoverFailed) {
      dispatch(
        handleWebHandover({
          express,
          basketId,
        }),
      );
    }
  }, []);

  useEffect(() => {
    if (isAppsAuthenticatedHandover) {
      if (typeof requestAccessToken === 'function' && !auth0AccessToken) {
        requestAccessToken();
        return;
      }

      if (auth0AccessToken) {
        window.timeToAuth0Complete = Date.now();
        dispatch(
          handleAppsHandover({
            accessToken: auth0AccessToken,
            actionType: APPS_AUTHENTICATED_HANDOVER,
            basketId,
            biom,
            environmentId: envId,
            express,
            jsessionId,
            monetateId,
            visitorId: vid,
            theme,
          }),
        );
      }
    }
  }, [auth0AccessToken, requestAccessToken, isAppsAuthenticatedHandover]);

  if (getLoginPageComplete) return children;

  // TODO remove this and pass a message prop from app.loadingMessage into
  // LoadingScreen component. Acceptance test will need fixing
  if (express) return null;

  return <LoadingScreen show />;
};

export default SessionManager;
